import { Injectable } from '@angular/core';
import {
  RoleplayCharacter,
  RoleplayCriterionScoreDto,
  RoleplayEndResponseDto,
  RoleplayFramesResponseDto,
  RoleplayIds,
  RoleplayMode,
  RoleplayStartRequestDto,
  RoleplayStartResponseDto,
  RoleplayTextTurnRequestDto,
  RoleplayTopicResponseDto,
  RoleplayTranscriptTurnDto,
  RoleplayTurnResponseDto,
} from '../models/roleplay.model';

/** Estado que el mock guarda por sesión abierta. */
interface MockSession {
  session_id: string;
  scenario_id: string;
  mode: RoleplayMode;
  /** Para calcular el cierre por `time_limit`. */
  started_at: number;
  /** Cuenta TODOS los mensajes (personaje + usuario), igual que la API. */
  turn_index: number;
  max_turns: number;
  max_duration_seconds: number;
  ended: boolean;
  /**
   * La conversación tal como fue ocurriendo, para poder devolverla en `/end`.
   * Se acumula en vez de reconstruirse del guion porque en modalidad chat el
   * texto del usuario es el que él escribió, y eso no está en ningún guion.
   */
  transcript: RoleplayTranscriptTurnDto[];
}

/**
 * Motor de conversación simulado para la evaluación práctica.
 *
 * Existe solo mientras backend termina las APIs, y su contrato es **el mismo**
 * que el de los endpoints reales: cada método de aquí devuelve exactamente el
 * DTO que devuelve su endpoint en `ApiService` (`iniciarSesionRoleplay`,
 * `enviarTurnoTextoRoleplay`, …). Por eso `RoleplayService` mapea una sola vez
 * y apagar el mock es cambiar un booleano.
 *
 * Lo que NO simula: los intentos y la configuración del roleplay. Eso no viaja
 * por estas APIs, llega dentro del tema (`tema.roleplay`) y lo resuelven los
 * helpers de `RoleplayService`. El personaje y el rol del usuario sí se sirven
 * aparte con `getCharacter()`, como respaldo mientras responde
 * `GET roleplay/topics/{id}`.
 *
 * ⚠️ QUITAR cuando la API esté disponible.
 */
@Injectable({ providedIn: 'root' })
export class RoleplayMockService {
  /** Tope de interacciones que "manda" la API al abrir la sesión. */
  private readonly MAX_TURNS = 6;
  /** Ventana de la conversación en segundos, igual que la API. */
  private readonly MAX_DURATION_SECONDS = 180;

  /** Sesiones vivas de esta ejecución, indexadas por session_id. */
  private sessions = new Map<string, MockSession>();

  /**
   * Audio de relleno para las modalidades con voz.
   *
   * Es un MP3 real servido desde el CDN, igual que lo que mandará la API: así
   * el `<audio>` de la burbuja y el autoplay se prueban contra una URL remota
   * (descarga, latencia, CORS) y no contra un data URI que siempre está listo.
   */
  private readonly AUDIO_DEMO_URL =
    'https://sfo2.digitaloceanspaces.com/cursalab2-statics/producto/roleplay/6a7eae08103989a7ab5a419a/7467a70da2e4485bacd3e5298f771f36.mp3?X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=DO00DC2V46LMGZG228P3%2F20260814%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20260814T055722Z&X-Amz-Expires=21600&X-Amz-SignedHeaders=host&X-Amz-Signature=bb64b4344a8a7aab1f4abdebacd58b953154f9ba32a0c0c7d309be3587920799';

  /** Guiones del personaje. El índice 0 es el mensaje de apertura. */
  private readonly scripts: Record<RoleplayMode, string[]> = {
    chat: [
      '¡Miren, necesito que me atiendan ya y me den una respuesta seria! Soy Carlos Ramírez. Pagué un servicio prioritario garantizado para hoy en la mañana porque tengo la presentación anual con nuestros principales clientes e inversionistas, ¡y el pedido NO ha llegado! Esto es una falta de respeto total. El evento empieza en menos de dos horas y me están dejando con los brazos cruzados. ¿Dónde está mi paquete y cómo me van a resolver este desastre justo ahora?',
      'El número de rastreo es TRK-884920. Pero por favor, revise de inmediato en su sistema, El evento es en hora y media y necesito saber si el repartidor ya viene en camino o qué van a hacer para solucionarlo YA.',
      'Entiendo que lo está revisando, pero necesito algo concreto. ¿Me puede confirmar una hora exacta de entrega? Si no llega antes del evento, esto no me sirve de nada.',
      'Bien, agradezco que al menos me dé una respuesta clara. ¿Y qué compensación me ofrecen por el incumplimiento del servicio prioritario que pagué?',
      'De acuerdo, eso me parece razonable. Quiero que me lo confirmen por correo con el número de caso, por favor.',
      'Perfecto. Gracias por tomarse el tiempo de resolverlo. Quedo atento al correo.',
    ],
    voice: [
      '¡Hola! Estoy aquí buscando un perfume especial para mi mejor amiga. Quiero asegurarme de encontrar algo que le encante y que sea significativo para su cumpleaños. ¿Tienes alguna recomendación?',
      'El número es #23424df324, dame una solución.',
      'Entiendo, pero necesito una hora exacta. ¿A qué hora llega el repartidor? El evento no se puede mover.',
      'Está bien. ¿Y qué van a hacer respecto al cobro del servicio prioritario que claramente no se cumplió?',
      'Me parece correcto. Confírmemelo por escrito, por favor, con el número de caso.',
      'Perfecto, quedo atento. Gracias por la gestión.',
    ],
    video: [
      'Buenos días. Gracias por conectarte a esta reunión.\nHe estado revisando minuciosamente la propuesta comercial que nos enviaron, nos parecen muy sólidos y alineados con lo que buscamos en la compañía.\nSin embargo, voy a ser muy directo contigo: La tarifa que nos planteas supera por mucho nuestro presupuesto.\n¿De qué manera podemos revisar los números para llegar a ese objetivo y cerrar el acuerdo?',
      'Disculpa, no te escuché con claridad. ¿A qué te refieres exactamente? ¿Tienes alguna propuesta alternativa o un ajuste en el alcance para que podamos acercarnos al presupuesto que requerimos?',
      'Entiendo tu punto. Ahora bien, si reducimos el alcance, ¿qué garantías tengo de que los tiempos de entrega se mantienen? Ese es un factor crítico para nosotros.',
      'Suena razonable. ¿Y ese ajuste incluye el soporte post implementación o eso se cotiza aparte?',
      'Perfecto. Necesito llevar esto al comité. ¿Puedes enviarme la propuesta revisada con esos términos antes del viernes?',
      'Excelente. Con eso creo que tenemos una base sólida para avanzar. Gracias por la flexibilidad.',
    ],
    // La modalidad streaming no pasa por el mock (ver `RoleplayService.usarMock`):
    // su conversación la genera OpenAI en vivo. Las llaves existen solo para
    // satisfacer al `Record<RoleplayMode, …>`.
    streaming: [],
  };

  /** Transcripciones simuladas: es el `trainee_transcript` de voz y video. */
  private readonly fakeTranscripts: Record<RoleplayMode, string[]> = {
    chat: [],
    voice: [
      'cual es el numero de seguimiento',
      'ya lo estoy revisando en el sistema, deme un momento por favor',
      'el repartidor llega en cuarenta minutos aproximadamente',
      'le vamos a reembolsar el costo del servicio prioritario',
      'le envío ahora mismo el correo con el número de caso',
      'gracias a usted por la paciencia',
    ],
    video: [
      'Tengo una promoción que te puede interesar',
      'Podemos ajustar el alcance para reducir el costo total',
      'Los tiempos de entrega se mantienen, están garantizados por contrato',
      'El soporte post implementación va incluido los primeros tres meses',
      'Te envío la propuesta revisada el jueves',
      'Gracias a ti por la apertura',
    ],
    streaming: [],
  };

  /**
   * Título del escenario por modalidad. Es lo único que el mock necesitaba del
   * viejo `getPracticalEvaluation()`: en producción el título llega dentro del
   * tema (`tema.roleplay.scenario.title`).
   */
  private readonly scenarioTitleByMode: Record<RoleplayMode, string> = {
    chat: 'Atención a Cliente Molesto por Demora',
    voice: 'Atención a Cliente Molesto por Demora',
    video: 'Negociación Comercial B2B',
    streaming: 'Negociación Comercial B2B',
  };

  private readonly charactersByMode: Record<
    RoleplayMode,
    { personaje: RoleplayCharacter; user_role: string }
  > = {
    chat: {
      personaje: { nombre: 'Carlos Ramírez', rol: '' },
      user_role: '',
    },
    voice: {
      personaje: { nombre: 'Carlos Ramírez', rol: '' },
      user_role: '',
    },
    video: {
      personaje: { nombre: 'Ing. Roberto Mendoza', rol: '' },
      user_role: '',
    },
    /**
     * Respaldo mientras `GET roleplay/topics/{id}` no responde: en streaming el
     * nombre del personaje encabeza el orbe, así que dejarlo vacío se nota.
     */
    streaming: {
      personaje: { nombre: 'Laura', rol: '' },
      user_role: '',
    },
  };

  // ── metadata del tema (no viaja por las APIs del roleplay) ─────────────────

  /** Personaje y rol del usuario. También vendrán dentro del tema. */
  getCharacter(modo: RoleplayMode): { personaje: RoleplayCharacter; user_role: string } {
    const { personaje, user_role } = this.charactersByMode[modo];
    return { personaje: { ...personaje }, user_role };
  }

  // ── espejo de las APIs ─────────────────────────────────────────────────────

  /**
   * Espejo de `ApiService.obtenerTopicRoleplay`.
   *
   * El tema se ignora (el mock no tiene catálogo de escenarios): el guion sale
   * de la modalidad, igual que el resto del mock. Tarda a propósito más que
   * `iniciarSesion` para que se vean los skeletons de la cabecera y del autor de
   * las burbujas mientras la conversación ya está abierta.
   */
  async obtenerTopic(
    _topic_id: string | number,
    modo: RoleplayMode
  ): Promise<RoleplayTopicResponseDto> {
    await this.delay(1500);

    const { personaje } = this.charactersByMode[modo];

    return {
      is_evaluable: true,
      grade_mode: 'averaged_with_exam',
      student_mode: modo,
      // Control de abandono encendido y con un tope corto a propósito: con 2
      // salidas, la tercera ya bloquea y la prueba manual no se hace eterna.
      // Anidado como lo manda backend de verdad.
      evaluation_settings: {
        not_allowed_change_page: true,
        quantity_attempts_to_change_page: 2,
      },
      scenario: {
        id: `mock-scenario-${modo}`,
        title: this.scenarioTitleByMode[modo],
        scenario_context:
          'El interlocutor llega molesto por un incumplimiento y espera una solución concreta.',
        learning_objectives: [
          'Reconocer la molestia antes de pedir datos operativos',
          'Ofrecer una solución concreta con plazo',
        ],
        difficulty: 'medium',
        max_turns: this.MAX_TURNS,
        max_duration_seconds: this.MAX_DURATION_SECONDS,
        persona: {
          name: personaje.nombre,
          role_description: modo === 'video' ? 'Gerente de compras escéptico' : 'Cliente frustrado',
          personality_traits: ['directo', 'impaciente'],
        },
      },
    };
  }

  /** Espejo de `ApiService.iniciarSesionRoleplay`. */
  async iniciarSesion(data: RoleplayStartRequestDto): Promise<RoleplayStartResponseDto> {
    await this.delay(600);

    const session: MockSession = {
      session_id: `mock-${data.mode}-${Date.now()}`,
      scenario_id: data.scenario_id,
      mode: data.mode,
      started_at: Date.now(),
      turn_index: 0,
      max_turns: this.MAX_TURNS,
      max_duration_seconds: this.MAX_DURATION_SECONDS,
      ended: false,
      transcript: [],
    };
    this.sessions.set(session.session_id, session);

    const apertura = this.scripts[session.mode][0];
    session.transcript.push(this.turnoTranscrito(session, 'persona', apertura));

    return {
      session_id: session.session_id,
      opening: {
        text: apertura,
        audio_url: this.audioUrlPara(session.mode),
      },
      mode: session.mode,
      max_turns: session.max_turns,
      max_duration_seconds: session.max_duration_seconds,
    };
  }

  /** Espejo de `ApiService.enviarTurnoTextoRoleplay`. */
  async enviarTurnoTexto(
    session_id: string,
    data: RoleplayTextTurnRequestDto
  ): Promise<RoleplayTurnResponseDto> {
    const session = this.obtenerSesion(session_id);
    await this.delay(900);
    return this.construirTurno(session, data.text);
  }

  /**
   * Espejo de `ApiService.enviarTurnoMediaRoleplay`. El archivo se ignora: lo
   * que en producción devuelve la transcripción aquí sale del guion.
   */
  async enviarTurnoMedia(
    session_id: string,
    _archivo: Blob,
    _fileName: string
  ): Promise<RoleplayTurnResponseDto> {
    const session = this.obtenerSesion(session_id);
    // Tarda más que el texto porque en producción hay una transcripción de por medio.
    await this.delay(1800);

    const transcripciones = this.fakeTranscripts[session.mode];
    const indice = Math.min(session.turn_index / 2, transcripciones.length - 1);
    return this.construirTurno(session, transcripciones[indice] ?? '');
  }

  /**
   * Espejo de `ApiService.enviarFramesRoleplay`. Las imágenes se descartan: el
   * log es lo único que permite verificar la cadencia mientras el mock esté
   * encendido.
   */
  async enviarFrames(session_id: string, frames: string[]): Promise<RoleplayFramesResponseDto> {
    this.obtenerSesion(session_id);
    await this.delay(200);
    console.log(`[roleplay-mock] frames recibidos: ${frames.length}`);
    return { expressions: { score: 0.8 } };
  }

  /**
   * Espejo de `ApiService.finalizarSesionRoleplay`.
   *
   * ⚠️ Incluye las llaves que backend todavía no manda (transcripción, métricas
   * de la sesión, nota en la escala del tema, QR, intentos). Están declaradas
   * como opcionales en `RoleplayEndResponseDto` con su `TODO(api)`: acá se
   * producen para que la pantalla de resultados sea recorrible entera.
   */
  async finalizarSesion(session_id: string, ids: RoleplayIds): Promise<RoleplayEndResponseDto> {
    const session = this.obtenerSesion(session_id);
    await this.delay(500);

    const conAudio = session.mode !== 'chat';
    const { personaje, user_role } = this.charactersByMode[session.mode];
    const turnosUsados = Math.floor(session.turn_index / 2);
    const transcurrido = Math.round((Date.now() - session.started_at) / 1000);

    return {
      session_id: session.session_id,
      scenario_id: session.scenario_id,
      workspace_id: ids.workspace_id,
      customer_id: ids.customer_id,
      user_id: ids.user_id,
      overall_score: 0.72,
      rubric_score: 0.72,
      intonation: conAudio ? 0.68 : null,
      expressions: session.mode === 'video' ? 0.75 : null,
      passed: true,
      passing_threshold: 0.6,
      criteria_scores: this.RUBRICA_DEMO,
      strengths: this.FORTALEZAS_DEMO,
      areas_for_improvement: this.MEJORAS_DEMO,
      summary_feedback: this.RESUMEN_DEMO,
      scored_by_model: 'gpt-4o-mini',
      id: `mock-result-${session.session_id}`,

      // ── llaves pendientes de backend, ver TODO(api) del DTO ─────────────────
      transcript: [...session.transcript],
      turns_used: turnosUsados,
      max_turns: session.max_turns,
      elapsed_seconds: transcurrido,
      max_duration_seconds: session.max_duration_seconds,

      persona_name: personaje.nombre,
      persona_role_description:
        session.mode === 'video' ? 'Gerente de compras escéptico' : 'Cliente frustrado',
      trainee_role: user_role,

      attempts_used: 1,
      max_attempts: 3,
      // La nota en la escala del tema tiene que cuadrar con `overall_score`, o
      // la pantalla muestra un 72% junto a un 9/20 y parece un bug.
      grade: Math.round(0.72 * 20),
      maximun_grade: 20,
      topic_name: this.scenarioTitleByMode[session.mode],
      course_name: 'Excelencia en Atención al Cliente',
      evaluated_at: new Date().toISOString(),
      active_results: true,
      validator: {
        image_qr: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcSeOJNgk6WKXImH1jr0cm4W6yFRUi-8iHWg27hflej5Z2OFGTy4nK8GJASs&s=10",
        identifier: `00912830${turnosUsados}${session.mode.slice(0, 3)}`,
      },

      // Los tres van apagados a propósito: sus botones navegan a ids que el
      // mock no puede inventar (tema siguiente, curso siguiente, certificado),
      // así que solo quedaría un enlace roto. Ponerlos a mano si hace falta ver
      // la botonera completa.
      next_topic_id: null,
      next_course_id: null,
      show_certification_to_user: false,
      pending_survey: false,
    };
  }

  // ── espejo de las APIs del roleplay libre (`source: 'free'`) ───────────────

  /**
   * Espejo de `ApiService.obtenerRoleplayLibre`.
   *
   * Mismo escenario que el del tema: el mock no tiene catálogo, y para la
   * pantalla de la conversación los dos orígenes son indistinguibles.
   */
  async obtenerRoleplayLibre(
    roleplay_id: string | number,
    modo: RoleplayMode
  ): Promise<RoleplayTopicResponseDto> {
    return this.obtenerTopic(roleplay_id, modo);
  }

  /**
   * Espejo de `ApiService.iniciarSesionRoleplayLibre`.
   *
   * Abre exactamente la misma sesión que la del tema: lo que cambia entre los
   * dos orígenes es de dónde se pide, no lo que se devuelve.
   */
  async iniciarSesionLibre(
    roleplay_id: string | number,
    modo: RoleplayMode
  ): Promise<RoleplayStartResponseDto> {
    return this.iniciarSesion({
      customer_id: '',
      user_id: '',
      workspace_id: '',
      scenario_id: `mock-scenario-${roleplay_id}`,
      mode: modo,
    });
  }

  // ── rúbrica de demo ────────────────────────────────────────────────────────
  //
  // La usa `finalizarSesion()`, el resultado recién puntuado.

  private readonly RUBRICA_DEMO: RoleplayCriterionScoreDto[] = [
    {
      key: 'bienvenida_calida',
      label: 'Bienvenida cálidaaa',
      score: 0.3,
      weight: 0.2,
      rationale: 'No saludaste y te presentaste antes de pedir datos.',
      evidence_quotes: ['Buenas tardes, lamento mucho lo ocurrido'],
      how_to_improve: 'Reconoce la molestia de forma explícita antes de pasar a la gestión.',
      example_better_response:
        'Buenas tardes, entiendo lo incómodo de la situación y voy a revisarlo ahora mismo con usted.',
    },
    {
      key: 'preguntas_abiertas',
      label: 'Preguntas abiertas',
      score: 0.6,
      weight: 0.25,
      rationale: 'Preguntaste por el número de rastreo, pero no exploraste el impacto real del retraso.',
      evidence_quotes: ['¿Cuál es el número de seguimiento?'],
      how_to_improve: 'Suma preguntas que te den contexto además del dato operativo.',
      example_better_response: '¿A qué hora necesita el paquete y qué parte del evento depende de él?',
    },
    {
      key: 'escucha_activa',
      label: 'Escucha activa',
      score: 0.75,
      weight: 0.2,
      rationale: 'Retomaste los datos que te dio el interlocutor al confirmar la gestión.',
      evidence_quotes: ['El evento es en hora y media'],
      how_to_improve: 'Parafrasea lo que te dicen antes de responder para confirmar que entendiste.',
      example_better_response:
        'Entonces necesita el material antes de las 3:00 p.m. para el evento; déjeme confirmarlo.',
    },
    {
      key: 'manejo_objeciones',
      label: 'Manejo de objeciones',
      score: 0.65,
      weight: 0.15,
      rationale: 'Respondiste a la objeción del cobro, aunque tardaste en ofrecer alternativas.',
      evidence_quotes: [],
      how_to_improve: 'Ofrece la alternativa en el mismo mensaje en el que reconoces el problema.',
      example_better_response:
        'Le reembolsamos el cargo del servicio prioritario y le mantengo el envío sin costo.',
    },
    {
      key: 'recomendaciones_personalizadas',
      label: 'Recomendaciones personalizadas',
      score: 0.7,
      weight: 0.1,
      rationale: 'Propusiste una solución acorde al plazo que necesitaba el cliente.',
      evidence_quotes: [],
      how_to_improve: 'Apoya la recomendación en los datos que el propio cliente te dio.',
      example_better_response:
        'Como el evento es a las 3:00 p.m., le programo una entrega express para las 2:00 p.m.',
    },
    {
      key: 'cierre_natural',
      label: 'Cierre natural',
      score: 0.8,
      weight: 0.1,
      rationale: 'Cerraste confirmando el acuerdo por escrito y con número de caso.',
      evidence_quotes: ['Le envío el correo con el número de caso'],
      how_to_improve: 'Añade un siguiente paso concreto con fecha y responsable.',
      example_better_response:
        'Le llega el correo en 10 minutos y yo mismo le confirmo la entrega apenas salga el repartidor.',
    },
  ];
  private readonly FORTALEZAS_DEMO: string[] = [
    'Mantuviste la calma frente a un interlocutor alterado.',
    'Cerraste con un compromiso concreto y verificable.',
  ];
  private readonly MEJORAS_DEMO: string[] = [
    'Reconocer la molestia antes de pedir datos operativos.',
    'Hacer preguntas abiertas que aporten contexto.',
    'Ofrecer la compensación sin esperar a que el cliente la pida.',
  ];
  private readonly RESUMEN_DEMO =
    'Manejaste la situación con calma y ofreciste una solución concreta. Puedes mejorar reconociendo antes la molestia del interlocutor y adelantándote a sus objeciones.';

  // ── helpers ────────────────────────────────────────────────────────────────

  private obtenerSesion(session_id: string): MockSession {
    const session = this.sessions.get(session_id);
    if (!session) {
      throw new Error(`Sesión de roleplay no encontrada: ${session_id}`);
    }
    return session;
  }

  /**
   * Arma la respuesta de un turno con las mismas reglas de cierre que la API:
   * primero manda el tiempo (`time_limit`) y después el tope de interacciones
   * (`turn_limit`). Como en la API, `persona_text` llega incluso en el turno
   * que cierra la conversación.
   */
  private construirTurno(session: MockSession, transcript: string): RoleplayTurnResponseDto {
    session.turn_index += 2;

    const interacciones = session.turn_index / 2;
    const guion = this.scripts[session.mode];
    const persona_text = guion[interacciones] ?? guion[guion.length - 1];

    const segundos = (Date.now() - session.started_at) / 1000;
    const porTiempo = segundos > session.max_duration_seconds;
    const porTurnos = interacciones >= session.max_turns;

    session.ended = porTiempo || porTurnos;

    session.transcript.push(
      this.turnoTranscrito(session, 'trainee', transcript),
      this.turnoTranscrito(session, 'persona', persona_text)
    );

    return {
      trainee_transcript: transcript,
      persona_text,
      persona_audio_url: this.audioUrlPara(session.mode),
      turn_index: session.turn_index,
      session_ended: session.ended,
      end_reason: porTiempo ? 'time_limit' : porTurnos ? 'turn_limit' : null,
    };
  }

  /**
   * Un turno para la transcripción que devuelve `/end`.
   * Solo el personaje trae audio; el del usuario se manda a transcribir y no se
   * conserva, igual que en producción.
   */
  private turnoTranscrito(
    session: MockSession,
    role: 'persona' | 'trainee',
    text: string
  ): RoleplayTranscriptTurnDto {
    return {
      role,
      text,
      audio_url: role === 'persona' ? this.audioUrlPara(session.mode) : null,
      created_at: new Date().toISOString(),
      is_transcript: role === 'trainee' && session.mode !== 'chat',
    };
  }

  /**
   * QR de relleno como data URI.
   *
   * Backend manda el QR ya renderizado (`validator.image_qr`), igual que en la
   * evaluación teórica, así que el mock solo necesita algo con la pinta correcta
   * para validar el encuadre. No codifica nada: es un patrón determinista a
   * partir del id de sesión, y va en data URI para no depender de la red.
   */
  private qrDemo(seed: string): string {
    const MODULOS = 21;
    let hash = 0;
    for (const caracter of seed) {
      hash = (hash * 31 + caracter.charCodeAt(0)) >>> 0;
    }

    let celdas = '';
    for (let fila = 0; fila < MODULOS; fila++) {
      for (let columna = 0; columna < MODULOS; columna++) {
        // Las tres esquinas de referencia van sólidas, como en un QR real.
        const enEsquina =
          (fila < 7 && columna < 7) ||
          (fila < 7 && columna >= MODULOS - 7) ||
          (fila >= MODULOS - 7 && columna < 7);
        const borde = fila % 6 === 0 || columna % 6 === 0;
        const pintar = enEsquina
          ? borde || (fila > 1 && fila < 5 && columna > 1 && columna < 5)
          : ((hash >> (fila + columna) % 32) ^ (fila * MODULOS + columna)) % 3 === 0;

        if (pintar) celdas += `<rect x="${columna}" y="${fila}" width="1" height="1"/>`;
      }
    }

    const svg =
      `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${MODULOS} ${MODULOS}" shape-rendering="crispEdges">` +
      `<rect width="${MODULOS}" height="${MODULOS}" fill="#fff"/><g fill="#000">${celdas}</g></svg>`;

    return ``;
    // return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
  }

  /** En chat no hay audio; en voz y video la API siempre manda uno. */
  private audioUrlPara(modo: RoleplayMode): string | null {
    return modo === 'chat' ? null : this.AUDIO_DEMO_URL;
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}
